import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { clearSsoMismatch, selectAuth, signOut } from 'app/store/authSlice';
import SsoAccountMismatchScreen from 'app/auth/SsoAccountMismatchScreen';
import {
  MICROSOFT_UNIVERSAL_LOGOUT_URL,
  clearSsoSwitchMarker,
  logSsoMismatch,
  requestShellAccountSwitch,
  writeSsoSwitchMarker,
} from 'app/auth/ssoAccountVerification';
import { isMobileAppShell } from 'app/auth/nativePasskeyBridge';

/**
 * SEE-1350: renders the account-mismatch choice screen whenever the
 * AuthProvider gate found a divergence (authSlice.ssoMismatch). Mounted once
 * in App.js so behavior is identical on web and in the shell.
 *
 * "Use a different Microsoft account" is the persistent switch:
 * - shell: ask the native side to run its sheet chain (Microsoft sign-out →
 *   Cognito logout in the SHARED jar), then sign out locally; the login page
 *   auto-relaunches the SSO attempt from the sessionStorage marker.
 * - web: sign out (the hosted-UI redirect through Cognito /logout clears
 *   Cognito's cookie); the login page auto-relaunches. If the same wrong
 *   account answers again (single wrong Entra session), the gate re-renders
 *   this screen in its `repeat` variant, which leads with Microsoft's own
 *   sign-out page.
 */
function SsoAccountVerificationGate(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { ssoMismatch } = useAppSelector(selectAuth);
  // The overlay is the only thing covering the signed-in app. Clearing
  // ssoMismatch before the sign-out redirect lands flashed the dashboard
  // between this screen and Microsoft, so instead we swap to a "leaving now"
  // state and let the navigation unmount us.
  const [leavingFor, setLeavingFor] = useState<string | null>(null);

  const onContinue = useCallback(() => {
    if (!ssoMismatch) return;
    logSsoMismatch('continued', {
      typedEmail: ssoMismatch.typedEmail,
      authenticatedIdentity: ssoMismatch.authenticatedIdentity,
    });
    clearSsoSwitchMarker();
    dispatch(clearSsoMismatch());
  }, [dispatch, ssoMismatch]);

  const onUseDifferentAccount = useCallback(async () => {
    if (!ssoMismatch) return;
    logSsoMismatch('switch', {
      typedEmail: ssoMismatch.typedEmail,
      authenticatedIdentity: ssoMismatch.authenticatedIdentity,
    });
    writeSsoSwitchMarker({
      typedEmail: ssoMismatch.typedEmail,
      provider: ssoMismatch.provider,
      expectIdentity: ssoMismatch.authenticatedIdentity,
      phase: 'switching',
      startedAt: Date.now(),
    });
    setLeavingFor('microsoft');
    if (isMobileAppShell()) {
      // Shell chain first (clears Microsoft + Cognito cookies in the shared
      // jar); a timeout/old shell falls through — the retry then simply hits
      // whatever sessions remain and the gate catches a repeat.
      await requestShellAccountSwitch();
    }
    // Sign out; on web the hosted-UI redirect clears Cognito's cookie and
    // lands on /login, in the shell RequireAuth navigates there — either way
    // the login page sees the marker and relaunches the SSO attempt. We keep
    // ssoMismatch set so this overlay stays up until that navigation happens.
    dispatch(signOut());
  }, [dispatch, ssoMismatch]);

  const onSignOutOfMicrosoft = useCallback(() => {
    // `window.open` is a no-op inside the shell WebView (no multi-window
    // handling), which would make this button do nothing. The shell has a
    // better answer anyway: its sheet chain LEADS with Microsoft's own
    // sign-out page, so re-run it instead of opening a tab that never appears.
    if (isMobileAppShell()) {
      onUseDifferentAccount();
      return;
    }
    window.open(MICROSOFT_UNIVERSAL_LOGOUT_URL, '_blank', 'noopener');
  }, [onUseDifferentAccount]);

  const onBackToLogin = useCallback(() => {
    if (ssoMismatch) {
      logSsoMismatch('back', {
        typedEmail: ssoMismatch.typedEmail,
        authenticatedIdentity: ssoMismatch.authenticatedIdentity,
      });
    }
    clearSsoSwitchMarker();
    setLeavingFor('login');
    dispatch(signOut());
  }, [dispatch, ssoMismatch]);

  if (!ssoMismatch) return null;

  return (
    <SsoAccountMismatchScreen
      typedEmail={ssoMismatch.typedEmail}
      authenticatedIdentity={ssoMismatch.authenticatedIdentity}
      repeat={ssoMismatch.repeat}
      leavingFor={leavingFor}
      onContinue={onContinue}
      onUseDifferentAccount={onUseDifferentAccount}
      onSignOutOfMicrosoft={onSignOutOfMicrosoft}
      onBackToLogin={onBackToLogin}
    />
  );
}

export default SsoAccountVerificationGate;
